import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { fetchUserEvents, exportUserEventsCsv } from '../../services/adminEvents';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const AuditUserEvents = () => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ type: '', email: '', user_id: '', start: '', end: '' });
  const limit = 100;

  const load = async () => {
    try {
      const data = await fetchUserEvents({
        token,
        type: filters.type || undefined,
        email: filters.email || undefined,
        user_id: filters.user_id || undefined,
        start: filters.start || undefined,
        end: filters.end || undefined,
        limit,
        offset: page * limit
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setItems([]);
      setTotal(0);
    }
  };

  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token, page]);

  const onExport = async () => {
    try {
      const blob = await exportUserEventsCsv({ token, ...filters });
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'user_events.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {}
  };

  const pages = Math.ceil(total / limit);

  // Graph data (simple, basé sur la page courante)
  const countsByType = items.reduce((acc, it) => {
    const k = it.type || 'other';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const typeEntries = Object.entries(countsByType).sort((a,b) => b[1]-a[1]);
  const maxType = Math.max(1, ...typeEntries.map(([,v]) => v));

  // Derniers 7 jours
  const dayKey = (d) => new Date(d).toISOString().slice(0,10);
  const today = new Date();
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (6 - i));
    return dayKey(d);
  });
  const countsByDay = days.reduce((acc, k) => (acc[k]=0, acc), {});
  items.forEach(it => { if (it.created_at) { const k = dayKey(it.created_at); if (k in countsByDay) countsByDay[k]++; } });
  const maxDay = Math.max(1, ...Object.values(countsByDay));

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Journal utilisateurs</h1>
        <p className="text-sm text-muted-foreground">Filtrez et exportez les événements collectés (pages vues, connexions, inscriptions, paiements, erreurs).</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtres</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <Input placeholder="Type (page_view, login, ...)" value={filters.type} onChange={e => setFilters({ ...filters, type: e.target.value })} />
            <Input placeholder="Email (en clair)" value={filters.email} onChange={e => setFilters({ ...filters, email: e.target.value })} />
            <Input placeholder="User ID" value={filters.user_id} onChange={e => setFilters({ ...filters, user_id: e.target.value })} />
            <Input type="datetime-local" value={filters.start} onChange={e => setFilters({ ...filters, start: e.target.value })} />
            <Input type="datetime-local" value={filters.end} onChange={e => setFilters({ ...filters, end: e.target.value })} />
          </div>
          <div className="flex gap-2 justify-end mt-3">
            <Button variant="outline" onClick={() => { setPage(0); load(); }}>Appliquer</Button>
            <Button onClick={onExport}>Exporter CSV</Button>
          </div>
        </CardContent>
      </Card>

      {/* Graphiques simples */}
      <Card>
        <CardHeader>
          <CardTitle>Graphiques (échantillon page)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Répartition par type */}
            <div>
              <div className="text-sm font-medium mb-2">Répartition par type (page en cours)</div>
              <div className="space-y-2">
                {typeEntries.length === 0 && <div className="text-sm text-muted-foreground">Aucune donnée</div>}
                {typeEntries.map(([label, value]) => (
                  <div key={label} className="flex items-center gap-2">
                    <div className="w-28 text-xs text-muted-foreground truncate">{label}</div>
                    <div className="flex-1 h-3 bg-muted rounded">
                      <div className="h-3 bg-primary rounded" style={{ width: `${(value/maxType)*100}%` }} />
                    </div>
                    <div className="w-8 text-right text-xs">{value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Évolution 7 derniers jours */}
            <div>
              <div className="text-sm font-medium mb-2">Événements (7 derniers jours, échantillon)</div>
              <div className="flex items-end gap-2 h-32">
                {days.map((d) => (
                  <div key={d} className="flex-1 flex flex-col items-center">
                    <div className="w-full bg-muted rounded-t">
                      <div className="w-full bg-secondary rounded-t" style={{ height: `${(countsByDay[d]/maxDay)*100}%` }} />
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">{d.slice(5)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Événements ({total})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">User ID</th>
                  <th className="py-2 pr-3">Email (hash)</th>
                  <th className="py-2 pr-3">URL</th>
                  <th className="py-2 pr-3">Referrer</th>
                  <th className="py-2 pr-3">IP (tronquée)</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b last:border-0">
                    <td className="py-2 pr-3 whitespace-nowrap">{it.created_at ? new Date(it.created_at).toLocaleString() : ''}</td>
                    <td className="py-2 pr-3">{it.type}</td>
                    <td className="py-2 pr-3">{it.user_id || ''}</td>
                    <td className="py-2 pr-3 font-mono text-[11px]">{it.email_hash || ''}</td>
                    <td className="py-2 pr-3 max-w-[280px] truncate" title={it.url}>{it.url}</td>
                    <td className="py-2 pr-3 max-w-[200px] truncate" title={it.referrer}>{it.referrer}</td>
                    <td className="py-2 pr-3">{it.ip_truncated || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="flex items-center justify-between mt-3 text-sm">
              <div>Page {page + 1} / {pages}</div>
              <div className="flex gap-2">
                <Button variant="outline" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Précédent</Button>
                <Button variant="outline" disabled={page + 1 >= pages} onClick={() => setPage(p => p + 1)}>Suivant</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditUserEvents;